// slice.ts
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { SolutionSet } from '../../data/solutionSet'
import { saveDeploymentLite } from './thunks'
import { AppError } from '../../common/result'
import { COMPANY_CHANGE, LOGOUT } from '../globalActions'

export enum DeploymentSaveStatus {
  Idle = 'Idle',
  Pending = 'Pending',
  Success = 'Success',
  Failed = 'Failed'
}

export interface DeploymentLiteState {
  solutionSet: SolutionSet | null
  deploymentSitesIds: string[]
  deploymentSaveStatus: DeploymentSaveStatus
}

const initialState: DeploymentLiteState = {
  solutionSet: null,
  deploymentSitesIds: [],
  deploymentSaveStatus: DeploymentSaveStatus.Idle
}

export const deploymentLiteSlice = createSlice({
  name: 'deploymentLite',
  initialState,
  reducers: {
    setDeployedSolutionSet: (state, action: PayloadAction<SolutionSet | null>) => {
      state.solutionSet = action.payload
    },
    setDeploymentSitesIds: (state, action: PayloadAction<string[]>) => {
      state.deploymentSitesIds = action.payload
    },
    setRetrySaveStatus: (state) => {
      state.deploymentSaveStatus = DeploymentSaveStatus.Idle
    },
    setSaveInProgress: (state) => {
      state.deploymentSaveStatus = DeploymentSaveStatus.Pending
    },
    resetDeploymentLiteState: () => initialState
  },
  extraReducers: (builder) => {
    builder.addCase(saveDeploymentLite.fulfilled, (state, action) => {
      state.deploymentSaveStatus = action.payload instanceof AppError ? DeploymentSaveStatus.Failed : DeploymentSaveStatus.Success
    })
    builder.addCase(saveDeploymentLite.rejected, (state) => {
      state.deploymentSaveStatus = DeploymentSaveStatus.Failed
    })

    // Reset state on logout
    builder.addCase(LOGOUT, () => initialState)
    builder.addCase(COMPANY_CHANGE, () => initialState)
  }
})

export default deploymentLiteSlice.reducer
export const {
  setDeployedSolutionSet,
  setDeploymentSitesIds,
  resetDeploymentLiteState,
  setRetrySaveStatus,
  setSaveInProgress
} = deploymentLiteSlice.actions
